import { isOAuthProvider, type OAuthProvider } from "@lorevok/domain";
import type { Page } from "../types.js";
import { safeLocalStorageGet, safeLocalStorageRemove, safeLocalStorageSet } from "./storage.js";

// Signing in with Google or Discord leaves the site and comes back as a fresh
// page load on /signin. What the app needs afterwards is remembered here, just
// before leaving: which provider was used (analytics: login_completed /
// sign_up_completed carry it as `method`), and so whether an error in the
// return address is this sign-in's. Kept for 30 minutes — longer than any
// consent screen takes, short enough that an abandoned attempt never labels a
// later password sign-in.

const OAUTH_START_KEY = "lorvok:oauthStart";
const OAUTH_RETURN_PAGE_KEY = "lorvok:oauthReturnPage";
export const OAUTH_START_TTL_MS = 30 * 60 * 1000;

/** Remembers that a sign-in with `provider` is starting. */
export function rememberOAuthStart(provider: OAuthProvider, now: number = Date.now()): void {
  safeLocalStorageSet(OAUTH_START_KEY, JSON.stringify({ provider, at: now }));
}

/** The provider of a recent sign-in that has not come back yet, without forgetting it. */
export function peekOAuthStart(now: number = Date.now()): OAuthProvider | null {
  const raw = safeLocalStorageGet(OAUTH_START_KEY);
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw) as { provider?: unknown; at?: unknown };
    if (!isOAuthProvider(saved.provider) || typeof saved.at !== "number") return null;
    const age = now - saved.at;
    return age >= 0 && age <= OAUTH_START_TTL_MS ? saved.provider : null;
  } catch {
    return null;
  }
}

/**
 * Reads and forgets the pending sign-in's provider; null when there is none, or
 * it is stale. The page the visitor was sent from stays until `goAfterAuth`
 * takes it (see {@link takeOAuthReturnTarget}) — both run when the visitor is back.
 */
export function takeOAuthStart(now: number = Date.now()): OAuthProvider | null {
  const provider = peekOAuthStart(now);
  safeLocalStorageRemove(OAUTH_START_KEY);
  return provider;
}

/** Drops a pending sign-in (it failed, or the visitor signed in another way), and the page it would return to. */
export function forgetOAuthStart(): void {
  safeLocalStorageRemove(OAUTH_START_KEY);
  safeLocalStorageRemove(OAUTH_RETURN_PAGE_KEY);
}

/** An owner page a signed-out visitor was sent to sign in for, and the novel it was open on. */
export interface OAuthReturnTarget {
  page: "create" | "profile" | "editor";
  /** The novel the editor had open. */
  novelId?: string;
}

/**
 * The page to come back to after Google / Discord for a visitor who pressed the
 * button on the sign-in gate of an owner page (Create, My Novels, the editor).
 * A password sign-in on that gate simply lifts it and leaves the visitor where
 * they were; the provider's round trip loses the page, so it is kept. The
 * generation status page has no job to come back to: Create stands in for it.
 */
export function gatedPageTarget(page: Page, editingNovelId: string | null): OAuthReturnTarget | null {
  if (page === "create" || page === "status") return { page: "create" };
  if (page === "profile") return { page: "profile" };
  if (page === "editor" && editingNovelId) return { page: "editor", novelId: editingNovelId };
  return null;
}

/** Remembers where to go once back from the provider (nothing to remember: forgets any earlier one). */
export function rememberOAuthReturnTarget(target: OAuthReturnTarget | null, now: number = Date.now()): void {
  if (!target) safeLocalStorageRemove(OAUTH_RETURN_PAGE_KEY);
  else safeLocalStorageSet(OAUTH_RETURN_PAGE_KEY, JSON.stringify({ ...target, at: now }));
}

/** Reads and forgets the page to return to; null when there is none, or it is stale. */
export function takeOAuthReturnTarget(now: number = Date.now()): OAuthReturnTarget | null {
  const raw = safeLocalStorageGet(OAUTH_RETURN_PAGE_KEY);
  if (!raw) return null;
  safeLocalStorageRemove(OAUTH_RETURN_PAGE_KEY);
  try {
    const saved = JSON.parse(raw) as { page?: unknown; novelId?: unknown; at?: unknown };
    if (typeof saved.at !== "number") return null;
    const age = now - saved.at;
    if (age < 0 || age > OAUTH_START_TTL_MS) return null;
    if (saved.page === "create" || saved.page === "profile") return { page: saved.page };
    if (saved.page === "editor" && typeof saved.novelId === "string" && saved.novelId) {
      return { page: "editor", novelId: saved.novelId };
    }
    return null;
  } catch {
    return null;
  }
}

const CALLBACK_ERROR_PARAMS = ["error", "error_code", "error_description"];

/** The auth callback's error parameters, in the hash (implicit flow) or the query. */
function callbackParams(location: Pick<Location, "hash" | "search">): URLSearchParams[] {
  return [new URLSearchParams(location.hash.replace(/^#/, "")), new URLSearchParams(location.search)];
}

/**
 * Whether the address is a refused sign-in with Google or Discord coming back
 * (the visitor cancelled on the provider's page, or Supabase refused it): an
 * `error` in the callback parameters while a sign-in of ours is pending. An
 * expired email link returns the same parameters, so without a pending
 * sign-in it is not reported here.
 */
export function isOAuthCallbackError(
  location: Pick<Location, "hash" | "search"> = window.location,
  now: number = Date.now()
): boolean {
  if (!peekOAuthStart(now)) return false;
  return callbackParams(location).some((params) => CALLBACK_ERROR_PARAMS.some((name) => params.has(name)));
}

/**
 * Takes a reported OAuth error out of the address bar (and forgets the pending
 * sign-in), so a reload or a shared link does not show it again. Campaign tags
 * and anything else in the query stay.
 */
export function clearOAuthCallbackError(): void {
  forgetOAuthStart();
  const url = new URL(window.location.href);
  const hash = new URLSearchParams(url.hash.replace(/^#/, ""));
  const hadHashError = CALLBACK_ERROR_PARAMS.some((name) => hash.has(name));
  let changed = hadHashError;
  for (const name of CALLBACK_ERROR_PARAMS) {
    if (url.searchParams.has(name)) {
      url.searchParams.delete(name);
      changed = true;
    }
  }
  if (!changed) return;
  if (hadHashError) url.hash = "";
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

/**
 * The provider an account was CREATED with (Supabase's app_metadata.provider)
 * when it is Google or Discord; null for an email account, even one that later
 * linked Google. Decides whether finishing sign-up is a new account's sign-up.
 */
export function accountOAuthProvider(user: { app_metadata?: { provider?: unknown } } | null | undefined): OAuthProvider | null {
  const provider = user?.app_metadata?.provider;
  return isOAuthProvider(provider) ? provider : null;
}
